import React, { Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Loader from "../components/loader";
import { useAuth } from "../contexts/AuthContext";
import AccountSelector from "src/pages/onboarding/AccountSelector";
import PaymentCompletionGuard from "./guards/PaymentCompletionGuard";

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { authenticated, loading, organizations, noOrgSelected, login } =
    useAuth();
  const location = useLocation();

  try {
    console.debug("🔐 PrivateRoute check:", {
      authenticated,
      loading,
      organizationCount: organizations?.length || 0,
      noOrgSelected,
      currentPath: location.pathname,
    });

    if (loading) {
      console.log("⏳ Still loading authentication...");
      return <Loader />;
    }

    if (!authenticated) {
      console.log("❌ Not authenticated, triggering login...");
      login();
      return <Loader />;
    }

    // Special handling for onboarding-related routes (including payment-status)
    const isOnboardingRoute =
      location.pathname.startsWith("/accounts/") ||
      location.pathname.startsWith("/onboarding") ||
      location.pathname.includes("/payment-status");

    // Check if user has organizations - either loaded in state or cached in localStorage
    // This prevents redirect loops when organizations are being loaded asynchronously after auth
    let hasOrganizations = organizations && organizations.length > 0;

    // Fallback: Check localStorage for cached organizations if state is empty
    // This handles the case where organizations exist but haven't loaded into state yet
    if (!hasOrganizations) {
      try {
        const cachedOrgs = localStorage.getItem("userOrganizations");
        const currentOrg = localStorage.getItem("currentOrganization");

        if (cachedOrgs) {
          const parsedOrgs = JSON.parse(cachedOrgs);
          hasOrganizations = Array.isArray(parsedOrgs) && parsedOrgs.length > 0;
        } else if (currentOrg) {
          // If currentOrganization exists, user has at least one org
          hasOrganizations = true;
        }
      } catch (error) {
        console.warn(
          "⚠️ Failed to check localStorage for organizations:",
          error,
        );
      }
    }

    console.debug("🔍 Organization check:", {
      hasOrganizations,
      organizationsLength: organizations?.length,
      isOnboardingRoute,
      currentPath: location.pathname,
      hasCachedOrgs: !!localStorage.getItem("userOrganizations"),
      hasCurrentOrg: !!localStorage.getItem("currentOrganization"),
    });

    if (!hasOrganizations) {
      if (isOnboardingRoute) {
        console.log(
          "🚀 No organizations, already on onboarding route, allowing access",
        );
        // User is already on an onboarding route and has no orgs, let them through
        return <Suspense fallback={<Loader />}>{children}</Suspense>;
      } else {
        console.log("🔄 No organizations found, redirecting to onboarding...");
        return <Navigate to="/accounts/new" replace />;
      }
    }

    if (noOrgSelected && !isOnboardingRoute) {
      console.log("🏢 No organization selected, showing account selector...");
      return <AccountSelector />;
    }

    console.debug("✅ All checks passed, rendering protected content");
    // Wrap with PaymentCompletionGuard to check subscription status
    return (
      <PaymentCompletionGuard>
        <Suspense fallback={<Loader />}>{children}</Suspense>
      </PaymentCompletionGuard>
    );
  } catch (error) {
    console.error("❌ Error in PrivateRoute:", error);
    login();
    return <Loader />;
  }
};

export default PrivateRoute;
